import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { GroupLeadershipPanel } from '@/pages/groups/group-leadership-panel';
import { LeadershipSubnav } from '@/pages/people/leadership-subnav';

interface GroupDetail {
  id: string;
  name: string;
  type: 'manual' | 'template';
  category: 'committee' | 'group';
  description: string | null;
  isActive: boolean;
}

const groupListPath = (group: Pick<GroupDetail, 'type' | 'category'>): string => {
  if (group.category === 'committee') return '/committees';
  if (group.type === 'manual') return '/groups';
  return '/groups/auto-filled';
};

export const GroupLeadershipPage = () => {
  const { groupId = '' } = useParams();
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const membership = useCurrentMembership();

  const canManageLeaders = hasPermission(membership, PERMISSIONS.GROUPS_MANAGE_LEADERS);

  const group = useQuery({
    queryKey: ['groups', groupId],
    queryFn: () => api.get<GroupDetail>(`/groups/${groupId}`),
    enabled: Boolean(groupId),
  });

  if (group.isLoading) {
    return (
      <div>
        <LeadershipSubnav />
        <PageHeader
          title={t('nav.leadership')}
          breadcrumbs={[
            { label: t('nav.leadership'), to: '/people/leadership/council' },
            { label: tCommon('state.loading') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      </div>
    );
  }

  if (!group.data) {
    return (
      <div>
        <LeadershipSubnav />
        <PageHeader
          title={t('nav.leadership')}
          breadcrumbs={[
            { label: t('nav.leadership'), to: '/people/leadership/council' },
            { label: t('groupLeadership.notFound') },
          ]}
        />
        <p className="text-body-sm text-fg-secondary">{t('groupLeadership.notFound')}</p>
      </div>
    );
  }

  return (
    <div>
      <LeadershipSubnav />
      <PageHeader
        title={group.data.name}
        description={group.data.description ?? t('groupLeadership.description')}
        breadcrumbs={[
          { label: t('nav.leadership'), to: '/people/leadership/council' },
          { label: group.data.name },
        ]}
        headerActions={
          <Button variant="outline" onClick={() => void navigate(groupListPath(group.data))}>
            {t('groupLeadership.viewGroup')}
          </Button>
        }
      />

      <div className="mt-6">
        <GroupLeadershipPanel
          groupId={group.data.id}
          canManage={canManageLeaders}
          readOnly
        />
      </div>
    </div>
  );
};
